import { FGCustomPanel, FGEmpty, FGMultiSuggestInput, FGTextAreaInput, FGTextInput, FieldGroup } from "nsitools-react";
import * as React from "react";
import { useHistory } from "react-router";
import styled from "styled-components";
import * as Yup from "yup";

import { CountryApi, CountryDto, CountryTranslationDto, ReferentialItemDto } from "../../../../api";
import { ERoutes } from "../../../../ERoutes";
import { FGMultiTranslate, SharepointButton, SmallFormGenerator } from "../../../../components";
import { useGlobalData } from "../../../../contexts";
import { useApiService, useCrudApi, useTl } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";
import { CountryPedigreesList } from "./pedigreePrefix/CountryPedigreesList";
import { useReferential } from "../../../../hooks/useReferential";
import { nameof } from "../../../../utils";
import { IOptionProps } from "@blueprintjs/core";

export interface ICountryDetailPageProps {
  countryId?: number;
  edit?: boolean;
}

const TitleContainer = styled.div`
  display: flex;
`;

const StyledContainer = styled.div`
  & > * + * {
    margin-top: 2rem;
  }
`;

export const CountryDetailPage: React.FunctionComponent<ICountryDetailPageProps> = ({
  countryId = 0,
  edit = false,
}) => {
  const { t } = useTl();
  const api = useApiService(CountryApi);
  const history = useHistory();
  const { languages } = useGlobalData();

  const [allLanguagesReferential, allLanguagesReferentialLoading] = useReferential(
    (a) => a.getAllLanguages(),
    true,
    []
  );

  const [editMode, setEditMode] = React.useState(edit);

  const FormSchema = React.useMemo(() => {
    return Yup.object().shape({
      isoCode: Yup.string()
        .nullable()
        .required(t(ETLCodes.Required))
        .test("is-unique", t(ETLCodes.IsoCodeAlreadyExists), async function (value) {
          if (!value) return true;
          const res = await api.isoCodeUnique(countryId, value);
          return res.value;
        }),
      canineGlobalEmail: Yup.string().nullable().email(t(ETLCodes.InvalidEmail)),
      emailForJudgeInCompetition: Yup.string().nullable().email(t(ETLCodes.InvalidEmail)),
      emailForWorkAttest: Yup.string().nullable().email(t(ETLCodes.InvalidEmail)),
      emailForBeautyJudge: Yup.string().nullable().email(t(ETLCodes.InvalidEmail)),
      caninePostalCode: Yup.string().nullable().matches(new RegExp("([0-9]{4})"), t(ETLCodes.NotValidPostalCode)),
      countryTranslations: Yup.array().of(
        Yup.object().shape({
          text: Yup.string().nullable().required(t(ETLCodes.Required)),
        })
      ),
    });
  }, [api, countryId, t]);

  const { data, loading, deleteItem, deleting, saveItem, saving, refresh } = useCrudApi(
    {
      getApiFn: (id = countryId) =>
        id > 0
          ? api.get(id)
          : new CountryDto({
              languageIds: [],
              countryTranslations: [
                ...languages.map(
                  (l) =>
                    new CountryTranslationDto({
                      languageId: l.languageId,
                    })
                ),
              ],
            }).toJSON(),
      saveApiFn: (d: CountryDto) => api.save(d),
      onSavedRoute: (d) => `${ERoutes.country}/${d.countryId}/detail/edit`,
      deleteApiFn: (d) => api.delete(d.countryId),
      onDeletedRoute: () => ERoutes.country,
    },
    [countryId]
  );

  const [shpLoading, setShpLoading] = React.useState(false);
  const existsInShp = React.useMemo(() => !!data?.sharepointUri, [data?.sharepointUri]);
  const openOrCreateInShp = React.useCallback(async () => {
    setShpLoading(true);
    if (!!data?.sharepointUri) {
      window.open(data?.sharepointUri, "_blank");
      setShpLoading(false);
    } else {
      try {
        await api.createInSharepoint(countryId);
        refresh();
      } catch (e) {
        throw e;
      } finally {
        setShpLoading(false);
      }
    }
  }, [api, countryId, data?.sharepointUri, refresh]);
  console.log("langref", allLanguagesReferential);
  return (
    <StyledContainer>
      <SmallFormGenerator
        initialValues={data}
        onSubmit={saveItem}
        editMode={edit}
        validationSchema={FormSchema}
        loading={loading}
        onCancel={() => history.push(ERoutes.country)}
        onDelete={deleteItem}
        showDeleteButton={countryId > 0}
        saving={saving}
        deleting={deleting}
      >
        <FieldGroup
          columns={2}
          fieldsetProps={{
            title: (
              <TitleContainer>
                <img
                  src={data?.flagLink}
                  height={20}
                  width={20}
                  alt={`${data?.isoCode} flag`}
                  style={{ marginRight: "0.5rem", marginTop: "2px" }}
                />{" "}
                <div>{t(ETLCodes.Country)}</div>
              </TitleContainer>
            ),
            rightElement: countryId > 0 && (
              <SharepointButton existsInShp={existsInShp} loading={shpLoading} onClick={openOrCreateInShp} />
            ),
          }}
        >
          <FieldGroup>
            <FGMultiTranslate
              label={ETLCodes.Name}
              translationsArrayField="countryTranslations"
              translationField="text"
              translationDto={CountryTranslationDto}
            />

            <FGMultiSuggestInput
              label={t(ETLCodes.CountryLanguages)}
              items={allLanguagesReferential}
              loading={allLanguagesReferentialLoading}
              displayField={"label"}
              valueField={"value"}
              isFilterable={true}
              name="languageIds"
            />
          </FieldGroup>
          <FieldGroup>
            <FGTextInput name="isoCode" label={t(ETLCodes.IsoCode)} readonly />
            <FGTextInput name="isoAs400" label={t(ETLCodes.IsoAs400)} />
          </FieldGroup>
        </FieldGroup>
        <FieldGroup fieldsetProps={{ title: t(ETLCodes.Canine) }} columns={2}>
          <FGTextInput name="canineNumber" label={t(ETLCodes.Number)} />
          <FGTextInput name="canineStreet" label={t(ETLCodes.Street)} />
          <FGTextInput name="caninePostalCode" label={t(ETLCodes.PostalCode)} maxLength={4} />
          <FGTextInput name="canineCity" label={t(ETLCodes.City)} />
          <FGTextInput name="canineGlobalEmail" label={t(ETLCodes.GlobalEmail)} />
          <FGTextInput name="emailForJudgeInCompetition" label={t(ETLCodes.JudgeEmail)} />
          <FGTextInput name="emailForWorkAttest" label={t(ETLCodes.WorkbookEmail)} />
          <FGTextInput name="emailForBeautyJudge" label={t(ETLCodes.BeautyJudgeEmail)} />
          <FGEmpty />
          <FGTextAreaInput name="remark" label={t(ETLCodes.Remark)} />
        </FieldGroup>
        <FGCustomPanel>{(ctx) => <>{setEditMode(ctx.editMode)}</>}</FGCustomPanel>
      </SmallFormGenerator>
      <CountryPedigreesList countryId={countryId} editMode={editMode} />
    </StyledContainer>
  );
};
